import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, AlertCircle, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { usePayrollHoursText } from '../hooks/usePayrollHoursText';

interface PayrollEntry {
  id: string;
  resource_id: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  earnings?: number;
  notes?: string;
  hourly_rate?: number | null;
  rate_amount?: number | null;
}

interface PayrollChangeRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: PayrollEntry | null;
  onSubmit: (requestData: any) => Promise<void>;
}

interface RequestFormData {
  clockIn: string;
  clockOut: string;
  breakStart: string;
  breakEnd: string;
  notes: string;
  requestReason: string;
}

export default function PayrollChangeRequestModal({ 
  isOpen, 
  onClose, 
  entry, 
  onSubmit 
}: PayrollChangeRequestModalProps) {
  const [formData, setFormData] = useState<RequestFormData>({
    clockIn: '',
    clockOut: '',
    breakStart: '',
    breakEnd: '',
    notes: '',
    requestReason: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // Open animation
  useEffect(() => {
    if (isOpen && entry) {
      setIsAnimating(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen, entry]);

  const handleTransitionEnd = () => {
    if (!isVisible && !isOpen) {
      setIsAnimating(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 200);
  };

  const formatDateTimeForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const offset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - offset);
    return localDate.toISOString().slice(0, 16);
  };

  const convertToUTC = (dateTimeString: string): string | null => {
    if (!dateTimeString) return null;
    const localDate = new Date(dateTimeString);
    return localDate.toISOString();
  };

  // Initialize form data when entry changes
  useEffect(() => {
    if (entry) {
      setFormData({
        clockIn: formatDateTimeForInput(entry.clock_in),
        clockOut: formatDateTimeForInput(entry.clock_out),
        breakStart: formatDateTimeForInput(entry.break_start),
        breakEnd: formatDateTimeForInput(entry.break_end),
        notes: entry.notes || '',
        requestReason: ''
      });
      setError(null);
    }
  }, [entry]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculateTotals = () => {
    if (!formData.clockIn || !formData.clockOut) return { totalMinutes: 0, totalHours: 0, totalEarnings: 0 };

    const start = new Date(formData.clockIn);
    const end = new Date(formData.clockOut);
    let totalMs = end.getTime() - start.getTime();
    
    if (formData.breakStart && formData.breakEnd) {
      const breakStart = new Date(formData.breakStart);
      const breakEnd = new Date(formData.breakEnd);
      totalMs -= (breakEnd.getTime() - breakStart.getTime());
    }
    
    const totalMinutes = Math.max(0, Math.floor(totalMs / (1000 * 60)));
    const totalHours = totalMinutes / 60;
    // Estimate at the shift's snapshot rate; the server recomputes (incl. overtime) on approval.
    const totalEarnings = totalHours * (entry?.rate_amount ?? entry?.hourly_rate ?? 0);
    
    return { totalMinutes, totalHours, totalEarnings };
  };

  const { totalMinutes, totalHours, totalEarnings } = calculateTotals();
  const hoursText = usePayrollHoursText([entry?.total_minutes || 0, totalMinutes]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!entry) return;
    
    if (!formData.requestReason.trim()) {
      setError('Please provide a reason for the change request.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const requestData = {
        payroll_entry_id: entry.id,
        original_clock_in: entry.clock_in,
        original_clock_out: entry.clock_out,
        original_break_start: entry.break_start,
        original_break_end: entry.break_end,
        original_total_minutes: entry.total_minutes,
        original_total_earnings: entry.earnings,
        original_notes: entry.notes,
        requested_clock_in: convertToUTC(formData.clockIn),
        requested_clock_out: convertToUTC(formData.clockOut),
        requested_break_start: convertToUTC(formData.breakStart),
        requested_break_end: convertToUTC(formData.breakEnd),
        requested_notes: formData.notes || null,
        request_reason: formData.requestReason
      };

      await onSubmit(requestData);
      handleClose();
    } catch (err) {
      console.error('Error submitting change request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit change request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if ((!isOpen || !entry) && !isAnimating) return null;

  return createPortal(
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isVisible ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{ 
        zIndex: 100200,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 overflow-hidden ${
          isMobile 
            ? `fixed left-0 right-0 w-full ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isVisible ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100210,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0, maxWidth: '100vw' }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative flex-shrink-0">
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              Request Time Change
            </h2>
            {entry?.clock_in && (
              <p className="text-sm text-gray-500 mt-1">
                {format(new Date(entry.clock_in), 'EEEE, MMM d, yyyy')}
              </p>
            )}
          </div>

          {/* Content */}
          <form 
            id="time-change-form" 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto overflow-x-hidden bg-white"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-5`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Current Entry */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  Current Entry
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="min-w-0">
                    <span className="text-gray-500 text-xs">Clock In</span>
                    <div className="font-semibold text-gray-900 mt-0.5">{entry?.clock_in ? format(new Date(entry.clock_in), 'h:mm a') : '-'}</div>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-500 text-xs">Clock Out</span>
                    <div className="font-semibold text-gray-900 mt-0.5">{entry?.clock_out ? format(new Date(entry.clock_out), 'h:mm a') : '-'}</div>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-500 text-xs">Total Time</span>
                    <div className="font-semibold text-gray-900 mt-0.5">{hoursText(entry?.total_minutes || 0)}</div>
                  </div>
                  <div className="min-w-0">
                    <span className="text-gray-500 text-xs">Earnings</span>
                    <div className="font-bold text-green-600 mt-0.5">${(entry?.earnings || 0).toFixed(2)}</div>
                  </div>
                </div>
              </div>

              {/* Requested Changes - Clock In/Out */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Requested Changes
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">
                      <Clock className="h-3 w-3 inline mr-1" />
                      Clock In
                    </label>
                    <input
                      type="datetime-local"
                      name="clockIn"
                      value={formData.clockIn}
                      onChange={handleInputChange}
                      className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">
                      <Clock className="h-3 w-3 inline mr-1" />
                      Clock Out
                    </label>
                    <input
                      type="datetime-local"
                      name="clockOut"
                      value={formData.clockOut}
                      onChange={handleInputChange}
                      className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Break Time */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Break Time <span className="font-normal text-gray-400">(Optional)</span>
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">Break Start</label>
                    <input
                      type="datetime-local"
                      name="breakStart"
                      value={formData.breakStart}
                      onChange={handleInputChange}
                      className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">Break End</label>
                    <input
                      type="datetime-local"
                      name="breakEnd"
                      value={formData.breakEnd}
                      onChange={handleInputChange}
                      className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes <span className="font-normal text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border"
                  placeholder="Optional notes..."
                  style={{ fontSize: '16px' }}
                />
              </div>

              {/* Reason */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <FileText className="h-4 w-4 inline mr-1" />
                  Reason for Change Request *
                </label>
                <textarea
                  name="requestReason"
                  value={formData.requestReason}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none box-border"
                  placeholder="Please explain why you need to change this time entry..."
                  style={{ fontSize: '16px' }}
                />
              </div>

              {/* Preview */}
              {formData.clockIn && formData.clockOut && (
                <div className="bg-blue-50 rounded-[12px] border border-blue-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Requested Changes Preview</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="min-w-0">
                      <span className="text-blue-600 text-xs">Total Time</span>
                      <div className="font-semibold text-blue-900 mt-0.5">{hoursText(totalMinutes)}</div>
                    </div>
                    <div className="min-w-0">
                      <span className="text-blue-600 text-xs">Total Earnings</span>
                      <div className="font-bold text-blue-900 mt-0.5">${totalEarnings.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-white px-4 py-3 flex-shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))' }}>
            <div className="flex gap-2.5">
              <button
                type="button"
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 active:bg-gray-100 active:scale-[0.98] text-[15px] font-semibold transition-all duration-150 touch-manipulation whitespace-nowrap"
                onClick={handleClose}
                disabled={isSubmitting}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="time-change-form"
                className="flex-1 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl text-[15px] font-semibold shadow-sm transition-all duration-150 touch-manipulation flex items-center justify-center gap-1.5 whitespace-nowrap"
                disabled={isSubmitting || !formData.requestReason.trim()}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-1.5">
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin flex-shrink-0" />
                    <span>Submitting...</span>
                  </div>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
